import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useImpersonation } from '../hooks/useImpersonation';

export function ImpersonationBanner(): React.JSX.Element | null {
  const navigate = useNavigate();
  const { isImpersonating, impersonatedContext, originalUser, endImpersonation } =
    useImpersonation();

  if (!isImpersonating || !impersonatedContext) {
    return null;
  }

  const handleExitImpersonation = (): void => {
    endImpersonation();
    navigate('/companies');
  };

  const statusVariant = impersonatedContext.companyStatus === 'ACTIVE' ? 'default' : 'destructive';
  const statusLabel = impersonatedContext.companyStatus === 'ACTIVE' ? 'Active' : 'Suspended';

  return (
    <div
      className="w-full border-b border-amber-300 bg-amber-100 px-6 py-2 dark:border-amber-500/50 dark:bg-amber-500/20"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <span className="font-medium">
            Impersonating:{' '}
            <strong data-testid="impersonated-company-name">
              {impersonatedContext.companyName}
            </strong>
          </span>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground" data-testid="original-user-email">
            Original user: {originalUser?.email}
          </span>
          <Button variant="outline" size="sm" onClick={handleExitImpersonation} className="gap-2">
            <X className="h-4 w-4" />
            Exit Impersonation
          </Button>
        </div>
      </div>
    </div>
  );
}
